// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store"; // Adjust the import based on your file structure
import { CreateNewReservationData } from "../../api/types";
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { useCreateNewReservationMutation } from "../../api/reservations/reservationsApiSlice";
import { processReservationData } from "./processors";
import { reservationCreated } from "../slice/bookingCreation/bookingCreationSlice";
import { notifyBookingFailure } from "../helpers/failureNotification";
import { getTenantName, setTimeOnDate } from "../../util";
import { resolveRequestDates } from "./reservationWindow";
import { useSelectedLocationTimeZone } from "../../util/locationTimeZone";
import { selectRoomFamilyGroupId } from "./roomGroupId";
import { requestCarriesTimeOfDay } from "../../util/roomRequestShape";

// import { Reservation } from "../../types/bookingCreation";

/**
 * The `amount` a `POST reservation` request carries: what the order data
 * says, but never below 1. The server multiplies the item's price by it.
 *
 * `orderData.amount` is shared booking state, and the station flows set it to
 * 0 on their group step (`durationSelectedClear`) and back to 1 only when a
 * time or duration is picked. The room-family flows (apartment, venue
 * direct booking, via BookingFlowShell) never touch it, so a station flow
 * abandoned at its group step left a 0 behind that the next room booking
 * sent: a 0 € stay on the old server, a 400 since the 2026-11-03 hotfix.
 *
 * No caller of this flow means anything but 1: a station item's units travel
 * in `quantity`, a room stay's price in its dates, and `amount` is only ever
 * 0 or 1 in state (`assetCountSelected` is never dispatched). The keys flow,
 * which does send 0 and is priced by its windows, has its own creation flow
 * (`keyHandoverCreateReservationFlow`) and is not affected.
 */
export const requestAmount = (amount: number | null | undefined): number =>
  typeof amount === "number" && amount >= 1 ? amount : 1;

export const useCreateNewReservation = (
  useCase?: string,
  options?: {
    throwOnError?: boolean;
    /**
     * Room-family flows only (BookingFlowShell): book the group
     * `selectRoomFamilyGroupId` names — the selected room's own group first,
     * `assetGroupId` only as fallback — the same rule
     * `createVenueReservationFlow` uses. Opt-in, because `selectedApartment`
     * is room state that can outlive its flow: the station flows sharing this
     * hook must never book a stale room's group.
     */
    roomFamilyGroup?: boolean;
    /**
     * Only a flow with its own quantity step (SUP's board count) sends
     * `orderData.quantity`; every other flow books one unit. The quantity is
     * shared booking state that only SUP's step sets and that is reset only
     * by SUP's own steps (a new date, a new group) and a completed booking,
     * so a SUP flow left another way left its board count behind, and the next rental, storage, p2p or room booking booked and
     * charged that many units (hotfix 2026-11-03).
     */
    sendsQuantity?: boolean;
  }
) => {

  const assetGroupSelection = useSelector(
    (state: RootState) => state.bookingCreation.assetGroupId
  );
  const roomFamilyGroupId = useSelector(selectRoomFamilyGroupId);
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const [createNewReservationMutation] = useCreateNewReservationMutation();
  const dispatch = useDispatch();
  // The booked location's clock (#3219) — the server reads the window there.
  const timeZone = useSelectedLocationTimeZone();

  const groupId = options?.roomFamilyGroup
    ? roomFamilyGroupId
    : assetGroupSelection;

  const createNewReservation = async () => {
    try {
      if (!groupId || !orderData) {
        // Never a silent return: the caller would carry on as if a
        // reservation existed — the room flow then PATCHed and tried to pay a
        // reservation that was never created. Thrown into the catch below so
        // it is logged, tracked and told exactly like an API failure.
        throw new Error(
          `createNewReservation submitted without ${
            !groupId ? "a group id" : "order data"
          }`
        );
      }

      // The ONE window builder (reservationWindow.ts) — this flow used to
      // carry its own copy, which is how the room flow's dates went out as
      // the phone's midnight rather than the station's.
      let { fromDate, toDate } = resolveRequestDates(orderData, timeZone);

      // Wire 2 stage 2: venue direct booking books an hourly window — stamp
      // the picked start/end times exactly as the venue request flow does, so
      // the reservation the payment charges covers the same window a request
      // would have asked for.
      if (requestCarriesTimeOfDay(useCase)) {
        fromDate = setTimeOnDate(fromDate, orderData.startTime!, timeZone);
        toDate = setTimeOnDate(toDate, orderData.endTime!, timeZone);
      }

      const newReservation: Array<CreateNewReservationData> = [
        {
          groupId,
          amount: requestAmount(orderData.amount),
          fromDate: fromDate,
          toDate: toDate,
          isFullDay: false,
          quantity: options?.sendsQuantity ? orderData.quantity || 1 : 1,
          useCase: useCase ?? undefined,
        },
      ];

      // Call the mutation function
      const response = await createNewReservationMutation(
        newReservation
      ).unwrap();

      const processedReservation = processReservationData(response);
      if (processedReservation) {
        dispatch(reservationCreated({ reservation: processedReservation }));
      }

      return processedReservation;
    } catch (error) {
      const context = {
        useCase,
        groupId,
        fromDate: orderData?.checkInDate?.toISOString?.() ?? orderData?.date?.toISOString?.(),
        tenant: getTenantName(),
      };
      Logger.for("createNewReservation").error(error, context);
      ErrorTracker.trackException(error as Error, context);
      // Rethrow only when caller opts in. Default behavior remains a silent
      // `undefined` return so existing flows (roomRental, equipmentRental,
      // p2p, temporaryStorage/newBooking) keep their previous semantics.
      if (options?.throwOnError) {
        throw error;
      }
      // Only the swallowing path notifies (#1970). A caller that opted into
      // the rethrow handles the failure itself — sup renders a persistent
      // inline error with a retry in ConfirmationStep — and a toast on top of
      // that would say the same thing twice.
      notifyBookingFailure(dispatch, error);
      return undefined;
    }
  };

  return createNewReservation;
};
